import {
  MenuItem,
  OutlinedInput,
  Select,
  SelectProps, Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { SxProps, Theme } from "@mui/material/styles";
import { FC, memo } from "react";
import { Control, Controller, FieldError } from "react-hook-form";

interface IMuiFormSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  error?: FieldError;
  variant?: SelectProps["variant"];
  control: Control<any>;
  required?: boolean;
  size?: "m" | "s";
}

const styles: Record<string, SxProps<Theme>> = {
  formControl: {
    width: "100%",

    "& .MuiTypography-root": {
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "20px",
      color: "#25282D",
      marginBottom: "4px",
    },

    "& fieldset": {
      borderColor: "#E0E4EE",
    }
  },
  sizeS: {
    "& .MuiSelect-select": {
      padding: "8px 12px !important",
      fontSize: "12px !important",
      lineHeight: "16px !important",
    },
  },
  label: {
    "& span": {
      color: "#FF0000"
    }
  }
};

const MuiFormSelect: FC<IMuiFormSelectProps> = ({
  options,
  label,
  name,
  error,
  control,
  placeholder,
  size,
  required
}) => {
  return (
    <FormControl
      error={Boolean(error?.message)}
      sx={[styles.formControl, size === "s" ? styles.sizeS : {}] as SxProps<Theme>}
    >
      {label && (
        <Typography sx={styles.label}>
          {label}
          {required && <span>*</span>}
        </Typography>
      )}
      <Controller
        render={({ field }) => {
          return (
            <Select
              MenuProps={{ variant: "menu", disableScrollLock: true }}
              input={<OutlinedInput />}
              displayEmpty
              renderValue={(value) => {
                const label = options
                .find(option => String(option.value) === String(value))?.label;

                return label || <em>{placeholder}</em>;
              }}
              id={name}
              {...field}
            >
              <MenuItem disabled value="">
                <em>{placeholder}</em>
              </MenuItem>
              {options.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          );
        }}
        name={name}
        control={control}
      />
      {error?.message && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
};

export default memo(MuiFormSelect);
